import { Chip, Typography } from '@mui/material';
import type { ColumnDef } from '../common/tableFeatures';
import { clipped, dateSortValue, dateTime } from '../common/tableCells';
import type { AuditLog } from '../../types/api';

type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

const actionColor = (action: string): ChipColor => {
  if (action.includes('created')) return 'success';
  if (action.includes('deleted') || action.includes('deactivated')) return 'error';
  if (action.includes('rejected')) return 'error';
  if (action.includes('updated') || action.includes('changed')) return 'warning';
  if (action.includes('verification_started')) return 'warning';
  if (action.includes('verified')) return 'success';
  return 'default';
};

const resourceColor = (type: string): ChipColor => {
  switch (type) {
    case 'device': return 'primary';
    case 'command': return 'secondary';
    case 'user': return 'info';
    case 'enrollment_token': return 'warning';
    default: return 'default';
  }
};

/** TanStack column definitions for the audit log.
 *
 *  Sorting reorders the page that is loaded - the server owns paging through
 *  the full log, so a sort here is a view of these rows, not of all events. */
export const AUDIT_COLUMNS: ColumnDef<AuditLog>[] = [
  {
    id: 'timestamp',
    accessorFn: (row) => dateSortValue(row.timestamp),
    header: 'Timestamp',
    cell: ({ row }) => dateTime(row.original.timestamp),
    size: 170,
  },
  {
    id: 'user_id',
    accessorKey: 'user_id',
    header: 'User',
    cell: ({ row }) => clipped(row.original.user_id, 160),
    size: 170,
  },
  {
    id: 'action',
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => (
      <Chip
        label={row.original.action.replace(/_/g, ' ')}
        color={actionColor(row.original.action)}
        size="small"
      />
    ),
    size: 190,
  },
  {
    id: 'resource_type',
    accessorKey: 'resource_type',
    header: 'Resource Type',
    cell: ({ row }) => (
      <Chip
        label={row.original.resource_type}
        color={resourceColor(row.original.resource_type)}
        size="small"
        variant="outlined"
      />
    ),
    size: 150,
  },
  {
    id: 'resource_id',
    accessorKey: 'resource_id',
    header: 'Resource ID',
    cell: ({ row }) => clipped(row.original.resource_id, 180, { mono: true }),
    size: 200,
  },
  {
    id: 'ip_address',
    accessorFn: (row) => row.ip_address || '',
    header: 'IP Address',
    cell: ({ row }) => clipped(row.original.ip_address, 130, { mono: true }),
    size: 140,
  },
  {
    id: 'user_agent',
    accessorFn: (row) => row.user_agent || '',
    header: 'User Agent',
    cell: ({ row }) => clipped(row.original.user_agent, 220),
    size: 240,
  },
  {
    id: 'details',
    header: 'Details',
    cell: ({ row }) => {
      const { details } = row.original;
      if (!details || Object.keys(details).length === 0) return '-';
      return (
        // `pre` keeps its own white-space, so the table's nowrap does not
        // flatten the JSON.
        <Typography
          component="pre"
          sx={{ margin: 0, fontSize: '0.75rem', maxWidth: 300, overflow: 'auto' }}
        >
          {JSON.stringify(details, null, 2)}
        </Typography>
      );
    },
    enableSorting: false,
    size: 320,
  },
];
